import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import moment from "moment";

import { ModelArrayCast, ModelCast } from "shared/casts";
import { TaskTrackerTaskPriorityEnum, TaskTrackerTaskStatusEnum } from "shared/enums";

import { Model } from '../Model';

import { IUserModel, UserModel } from "../UserModel";
import { BoardModel, IBoardModel } from "./BoardModel";
import { ColumnModel, IColumnModel } from "./ColumnModel";
import { ISpaceModel, SpaceModel } from "./SpaceModel";
import { ITaskViewModel, TaskViewModel } from "./TaskViewModel";

const DEADLINE_DAYS_BY_PRIORITY: Record<string, number> = {
    [TaskTrackerTaskPriorityEnum.High.id]: 3,
    [TaskTrackerTaskPriorityEnum.Normal.id]: 7,
    [TaskTrackerTaskPriorityEnum.Low.id]: 14,
};

export interface ITaskModel {
    id?: number ;
    ownerUserId?: number | null;
    responsibleUserId?: number | null;
    columnId?: number | null;
    isSelected?: number,
    boardId?: number | null;
    spaceId?: number | null;
    sort?: number;
    name?: string;
    description?: string;
    createdAt?: string;
    expiredAt?: string;
    expiredAtPropose?: string | null;
    expiredAtProposeUserId?: number | null;
    expiredAtProposeComment?: string | null;
    expiredAtProposeIsAccepted?: boolean | null;
    expiredAtProposeIsAcceptedComment?: string | null;
    doneAt?: string | null;
    priorityId?: string;
    statusId?: string;
    usersIds?: number[];
    column?: IColumnModel,
    board?: IBoardModel,
    space?: ISpaceModel,
    users?: IUserModel[];
    taskViews?: ITaskViewModel[];
    files?: string[];
    responsibleUser?: IUserModel;
    ownerUser?: IUserModel;
    expiredAtProposeUser?: IUserModel;
}

export class TaskModel extends Model implements ITaskModel {
    casts = {
        users: new ModelArrayCast(UserModel),
        taskViews: new ModelArrayCast(TaskViewModel),
        board: new ModelCast(BoardModel),
        space: new ModelCast(SpaceModel),
        column: new ModelCast(ColumnModel),
        responsibleUser: new ModelCast(UserModel),
        ownerUser: new ModelCast(UserModel),
        expiredAtProposeUser: new ModelCast(UserModel),
    };

    id = 0;
    isSelected = 0;
    ownerUserId: number | null = null;
    responsibleUserId: number | null = null;
    columnId: number | null = null;
    boardId: number | null = null;
    spaceId: number | null = null;
    sort = 0;
    name = '';
    description = '';
    createdAt = moment().format();
    expiredAt = moment(this.createdAt)
        .add(DEADLINE_DAYS_BY_PRIORITY[TaskTrackerTaskPriorityEnum.Normal.id], 'days')
        .set('hours', 18)
        .set('minutes', 0)
        .format();
    expiredAtPropose: string | null = null;
    expiredAtProposeUserId: number | null = null;
    expiredAtProposeComment: string | null = null;
    expiredAtProposeIsAccepted: boolean | null = null;
    expiredAtProposeIsAcceptedComment: string | null = null;
    doneAt: string | null = null;
    priorityId = TaskTrackerTaskPriorityEnum.Normal.id;
    statusId = '';
    usersIds: number[] = [];
    users: UserModel[] = [];
    taskViews: TaskViewModel[] = [];
    column: ColumnModel = new ColumnModel();
    board: BoardModel = new BoardModel();
    space: SpaceModel = new SpaceModel();
    responsibleUser: UserModel = new UserModel();
    ownerUser: UserModel = new UserModel();
    expiredAtProposeUser: UserModel = new UserModel();
    files = [];

    constructor(payload: ITaskModel = {
    }) {
        super();

        makeObservable(this, {
            id: observable,
            ownerUserId: observable,
            responsibleUserId: observable,
            isSelected: observable,
            columnId: observable,
            boardId: observable,
            spaceId: observable,
            sort: observable,
            name: observable,
            description: observable,
            expiredAt: observable,
            createdAt: observable,
            doneAt: observable,
            priorityId: observable,
            priority: computed,
            statusId: observable,
            users: observable,
            usersIds: observable,
            column: observable,
            board: observable,
            space: observable,
            responsibleUser: observable,
            ownerUser: observable,
            files: observable,
            taskViews: observable,
            expiredAtPropose: observable,
            expiredAtProposeComment: observable,
            expiredAtProposeUserId: observable,
            expiredAtProposeUser: observable,
            expiredAtProposeIsAccepted: observable,
            expiredAtProposeIsAcceptedComment: observable,
            createdAtMoment: computed,
            expiredAtMoment: computed,
            minimumExpiredAtMoment: computed,
            minimumExpiredAtFromNowMoment: computed,
            expiredAtProposeMoment: computed,
            doneAtMoment: computed,
            expiredAtTime: computed,
            expiredAtProposeTime: computed,
            status: computed,
            statusText: computed,
            search: computed,
            update: action
        });

        this.update(payload);
    }

    handleChange = <R>(data: { name: string; value: R }) => {
        runInAction(() => {
            this[data.name] = data.value;

            if (data.name !== 'priorityId') {
                return;
            }

            if (!DEADLINE_DAYS_BY_PRIORITY[String(data.value)]) {
                return;
            }

            const currentDeadline = this.expiredAtMoment;
            this.expiredAt = moment()
                .add(DEADLINE_DAYS_BY_PRIORITY[String(data.value)], 'days')
                .set('hours', currentDeadline.hours())
                .set('minutes', currentDeadline.minutes())
                .set('seconds', currentDeadline.seconds())
                .format();
        });
    };

    get createdAtMoment() {
        if (!this.createdAt) {
            return moment();
        }

        return moment(this.createdAt);
    }

    get doneAtMoment() {
        if (!this.doneAt) {
            return moment();
        }

        return moment(this.doneAt);
    }

    get expiredAtMoment() {
        if (!this.expiredAt) {
            return moment();
        }

        return moment(this.expiredAt);
    }

    get minimumExpiredAtMoment() {
        const days = DEADLINE_DAYS_BY_PRIORITY[this.priorityId]
            ?? DEADLINE_DAYS_BY_PRIORITY[TaskTrackerTaskPriorityEnum.Normal.id];

        return this.createdAtMoment.clone().startOf('day').add(days, 'days');
    }

    get minimumExpiredAtFromNowMoment() {
        const days = DEADLINE_DAYS_BY_PRIORITY[this.priorityId]
            ?? DEADLINE_DAYS_BY_PRIORITY[TaskTrackerTaskPriorityEnum.Normal.id];

        return moment().startOf('day').add(days, 'days');
    }

    get expiredAtTime() {
        return this.expiredAtMoment.clone().format('HH:mm');
    }

    set expiredAtTime(value) {
        runInAction(() => {
            const [hours, minutes] = value.split(':');
            this.expiredAt = this.expiredAtMoment.clone().set('hours', +hours).set('minutes', +minutes).format();
        })
    }

    get expiredAtProposeMoment() {
        if (!this.expiredAtPropose) {
            return moment();
        }

        return moment(this.expiredAtPropose);
    }

    get expiredAtProposeTime() {
        return this.expiredAtProposeMoment.clone().format('HH:mm');
    }

    set expiredAtProposeTime(value) {
        runInAction(() => {
            const [hours, minutes] = value.split(':');
            this.expiredAtPropose = this.expiredAtProposeMoment.clone().set('hours', +hours).set('minutes', +minutes).format();
        })
    }

    get search() {
        let searchable = [this.name];
        if (this.ownerUser) {
            searchable.push(this.ownerUser.name);
        }
        if (this.responsibleUser) {
            searchable.push(this.responsibleUser.name);
        }
        searchable = [...searchable, ...this.users.map(user => user.name)];
        return searchable.join(' ');
    }

    get priority() {
        return TaskTrackerTaskPriorityEnum.from(this.priorityId);
    }

    get status() {
        const status = TaskTrackerTaskStatusEnum.from(this.statusId)
        if (status.in([TaskTrackerTaskStatusEnum.Done, TaskTrackerTaskStatusEnum.Delayed, TaskTrackerTaskStatusEnum.Archive])) {
            return status;
        }

        const duration = moment.duration(this.expiredAtMoment.diff(moment()));
        const hoursToExpired = duration.asHours();

        if (hoursToExpired <= 0) {
            return TaskTrackerTaskStatusEnum.from(TaskTrackerTaskStatusEnum.Expired.id);
        }

        if (hoursToExpired < 24) {
            return TaskTrackerTaskStatusEnum.from(TaskTrackerTaskStatusEnum.AlmostExpired.id);
        }

        return TaskTrackerTaskStatusEnum.from(TaskTrackerTaskStatusEnum.InWork.id);
    }

    get statusText() {
        if (this.status.is(TaskTrackerTaskStatusEnum.Done)) {
            return 'Завершен'
        }

        return this.expiredAtMoment.format('D MMMM, HH:mm');
    }

    canSave = (user: UserModel) => {
        // user.isAdmin
        return this.ownerUserId === user.id || this.space.spaceOwners.some(spaceOwner => +spaceOwner.userId === +user.id);
    }

    isSpaceOwner = (user: UserModel) => {
        return this.space.spaceOwners.some(spaceOwner => +spaceOwner.userId === +user.id);
    }

    isAssignmentOnly = (user: UserModel) => {
        if (!this.id || user.isAdmin || this.ownerUserId  === user.id) {
            return false;
        }

        return this.isSpaceOwner(user);
    }
}
